/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { cssMap, jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	setHubBrandingStyle,
	useHubContext,
	useNavigationStyle,
	useUnsetGlobalTheme,
} from '@atlassian/company-hub-common';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';

import { Banner } from './banner';
import { Navbar } from './navbar';

const containerStyles = cssMap({
	bannerContainer: {
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
	},
	container: {
		boxSizing: 'border-box',
		appearance: 'none',
		border: 'none',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		overflow: 'auto',
		minHeight: '100%',
		display: 'flex',
		flexDirection: 'column',
		// #ref - help-center/packages/common-utils/styles/src/global.tsx
		// we can't use imported tokens here so hard coding the value instead but this is from the @atlassian/company-hub-common package
		/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors */
		'& a:not(.cv-exclude-from-customisation):not([data-exclude-global-styling="true"])': {
			color: 'var(--hub-primary-link-color)',
		},
		'& a:hover:not(.cv-exclude-from-customisation):not([data-exclude-global-styling="true"])': {
			color: 'var(--hub-primary-link-color)',
		},
	},
	childrenWrapper: {
		paddingBottom: token('space.600'),
	},
	childrenWrapperEditMode: {
		// In edit mode, the wrapper must fill the remaining flex space so
		// that the editor's `height: 100%` chain resolves correctly.
		// Without this, the config panel loses its fixed height and scrolls
		// together with the page content instead of independently.
		flexGrow: 1,
		minHeight: '0px',
	},
});

export const HubAppShell = ({
	children,
	isEditMode = false,
	hideNavbar = false,
}: {
	children: React.ReactNode;
	/**
	 * When `true` the built-in Navbar is not rendered.  Use this together with
	 * the `editor-plugin-hub-shell` plugin which injects its own visual Navbar
	 * inside the editor content area so both elements resize together when the
	 * editor config panel opens.
	 */
	hideNavbar?: boolean;
	isEditMode?: boolean;
}): JSX.Element => {
	di(useHubContext, fg);
	const { layout, containerStyling, headerless } = useHubContext();
	const { options: navStyle } = useNavigationStyle();

	const fixedNavBarStyling = navStyle?.fixedAtTop ? { height: '100%' } : {};

	const isHeaderless = headerless && fg('csm-support-site-headerless-query-param');
	const showBanner = layout?.showBanner;
	const shouldApplyContainerStyling = fg('enable-jsm-in-hubs');

	// Default hardcoded styles (used when feature flag is disabled for backward compatibility)
	const defaultContainerStyling = {
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
	};

	// Apply containerStyling from props if feature flag is enabled, otherwise use hardcoded defaults
	const appliedContainerStyling = shouldApplyContainerStyling
		? containerStyling
		: defaultContainerStyling;

	const hubContainerRef = React.useRef<HTMLDivElement>(null);
	useUnsetGlobalTheme(isEditMode ? hubContainerRef.current : null); // for view mode we don't need to unset the global theme

	// useHubTheming returns undefined outside <HubThemingProvider>, but
	// HubAppShell is always mounted inside HubBootstrap → provider is in tree.
	const { primaryColor, isAdvancedThemingEnabled } = useHubTheming()!;

	// Legacy theming path: when the v2 gate is OFF we restore the pre-v2
	// behaviour by writing the `--hub-primary-*` CSS vars into the document so
	// existing ADFs (which encode `var(--hub-primary-color, ...)` strings) and
	// chrome that consumes the same vars continue to look correct. When the
	// gate is ON the palette + adapter outputs from `useHubTheming()` drive
	// every consumer directly, so this effect is a no-op.
	React.useEffect(() => {
		if (isAdvancedThemingEnabled) {
			return undefined;
		}
		return setHubBrandingStyle({ primaryColor });
	}, [isAdvancedThemingEnabled, primaryColor]);

	return (
		<Box
			xcss={containerStyles.container}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic styles passed from outside via containerStyling prop
			style={{ ...appliedContainerStyling, ...fixedNavBarStyling }}
			ref={hubContainerRef}
		>
			{!isHeaderless && !hideNavbar && <Navbar isEditMode={isEditMode} />}
			{showBanner && (
				<div css={containerStyles.bannerContainer}>
					<Banner />
				</div>
			)}
			<Box
				xcss={
					isEditMode ? containerStyles.childrenWrapperEditMode : containerStyles.childrenWrapper
				}
				testId="hub-app-shell-children-wrapper"
			>
				{children}
			</Box>
		</Box>
	);
};
